import { ETourId, type ITourConfig } from '../types';

/**
 * Welcome tour shown on the drafts list page on chat input area.
 *
 * A single-step intro that highlights the hero input area
 * and invites the user to start their first automation.
 */
export const draftWelcomeTour: ITourConfig = {
  id: ETourId.DRAFT_WELCOME,
  steps: [
    {
      id: 'draft-welcome',
      target: '[data-tour-id="draft-page-input"]',
      titleKey: 'tours.draftCreation.describeTask.title',
      descriptionKey: 'tours.draftCreation.describeTask.description',
      finishLabelKey: 'tours.draftCreation.welcome.finishLabel',
      previousLabelKey: 'tours.draftCreation.welcome.skipLabel',
      position: 'rightTop',
    },
  ],
  namespace: 'tours',
  variant: 'default',
  size: 'sm',
  alwaysShowPrevious: true,
  analytics: {
    enabled: true,
    eventPrefix: 'tour_draft_welcome',
    metadata: {
      tourName: 'Draft Welcome',
    },
  },
  persistence: {
    strategy: 'localStorage',
  },
};

/**
 * Editor tour shown on the draft creation chat page.
 *
 * Two steps:
 * 1. Connect an integration — fixed-position nudge near the chat
 * 2. Manage integrations — spotlights the sidebar Integrations tab
 *
 * TODO: Change "Next" to "Set it up" and on click, should
 * inject 'Help me set up an integration' into Quill.
 */
export const draftEditorTour: ITourConfig = {
  id: ETourId.DRAFT_EDITOR,
  steps: [
    {
      id: 'connect-integration',
      fixedPosition: 'bottom-left',
      titleKey: 'tours.draftCreation.connectIntegration.title',
      descriptionKey: 'tours.draftCreation.connectIntegration.description',
      previousLabelKey: 'tours.draftCreation.connectIntegration.skipLabel',
    },
    {
      id: 'manage-integrations',
      target: '[data-tour-id="sidebar-integrations-tab"]',
      titleKey: 'tours.draftChatIntegrationConnection.manage.title',
      descriptionKey: 'tours.draftChatIntegrationConnection.manage.description',
      finishLabelKey: 'tours.draftChatIntegrationConnection.manage.finishLabel',
      position: 'rightTop',
    },
  ],
  namespace: 'tours',
  variant: 'default',
  size: 'sm',
  alwaysShowPrevious: true,
  analytics: {
    enabled: true,
    eventPrefix: 'tour_draft_editor',
    metadata: {
      tourName: 'Draft Editor',
    },
  },
  persistence: {
    strategy: 'localStorage',
  },
};

/**
 * Automation edit view tour shown when the draft automation panel first appears.
 *
 * Three steps introducing the generated automation:
 * 1. Your draft workflow — the AOP display panel on the right
 * 2. Prepare to run — the Inputs & Triggers button
 * 3. Test your automation — the Run/Play button in the header
 */
export const draftAutomationEditViewTour: ITourConfig = {
  id: ETourId.DRAFT_AUTOMATION_EDIT_VIEW,
  steps: [
    {
      id: 'draft-workflow',
      target: '[data-tour-id="editor-panel"]',
      titleKey: 'tours.draftCreation.draftWorkflow.title',
      descriptionKey: 'tours.draftCreation.draftWorkflow.description',
      position: 'leftTop',
    },
    {
      id: 'prepare-to-run',
      target: '[data-tour-id="inputs-trigger-button"]',
      titleKey: 'tours.draftCreation.prepareToRun.title',
      descriptionKey: 'tours.draftCreation.prepareToRun.description',
      position: 'topLeft',
    },
    {
      id: 'test-automation',
      target: '[data-tour-id="run-automation-button"]',
      titleKey: 'tours.draftCreation.testAutomation.title',
      descriptionKey: 'tours.draftCreation.testAutomation.description',
      finishLabelKey: 'tours.draftCreation.testAutomation.finishLabel',
      position: 'bottomLeft',
    },
  ],
  namespace: 'tours',
  variant: 'default',
  size: 'sm',
  analytics: {
    enabled: true,
    eventPrefix: 'tour_draft_automation_edit_view',
    metadata: {
      tourName: 'Draft Automation Edit View',
    },
  },
  persistence: {
    strategy: 'localStorage',
  },
};

/**
 * Inputs & Triggers panel tour shown when the bottom sheet opens on a draft.
 *
 * Two steps guiding the user through the panel:
 * 1. This run needs a little setup first — the entire inputs panel
 * 2. Set up how runs start — the trigger type selector section
 */
export const inputsTriggersTour: ITourConfig = {
  id: ETourId.INPUTS_TRIGGERS,
  steps: [
    {
      id: 'inputs-setup',
      target: '[data-tour-id="inputs-triggers-panel"]',
      titleKey: 'tours.inputsTriggers.inputsSetup.title',
      descriptionKey: 'tours.inputsTriggers.inputsSetup.description',
      position: 'leftTop',
    },
    {
      id: 'trigger-setup',
      target: '[data-tour-id="trigger-type-selector"]',
      titleKey: 'tours.inputsTriggers.triggerSetup.title',
      descriptionKey: 'tours.inputsTriggers.triggerSetup.description',
      finishLabelKey: 'tours.inputsTriggers.triggerSetup.finishLabel',
      position: 'leftTop',
    },
  ],
  namespace: 'tours',
  variant: 'default',
  size: 'sm',
  analytics: {
    enabled: true,
    eventPrefix: 'tour_inputs_triggers',
    metadata: {
      tourName: 'Inputs & Triggers',
    },
  },
  persistence: {
    strategy: 'localStorage',
  },
};
